/**
 * THE FOUR SHIPPED EXAMPLE DOCUMENTS (SPEC.md §5.2, docs/DESIGN.md §6.7).
 *
 * Each dataset is a real `.json` file reached through a dynamic `import()`, so none of the
 * ~158 KB below is in the initial bundle: opening the dialog costs nothing, and picking one
 * example fetches one chunk. "Nothing is fetched" in the dialog subtitle stays literally
 * true — the chunk is part of the build output on the same origin, and there is no server
 * to ask (ADR-0001).
 *
 * WHY THE FACTS ARE DECLARED AND NOT COMPUTED. DESIGN §6.7 puts `84.2 KB / 2,140 lines` and
 * `depth 7 · 1,204 keys` on every row, because the metadata is what a developer actually
 * chooses by. Computing it would mean loading all four datasets to draw a list of four
 * names, which defeats the lazy import. So the numbers are literals — and
 * `examples.test.ts` loads every dataset and asserts each literal against the real value,
 * which turns "a stale constant" from a silent lie into a failing test.
 *
 * The text handed back is `JSON.stringify(data, null, 2)` plus the final newline of
 * `DEFAULT_FORMAT_OPTIONS`, i.e. byte for byte what pressing Format would produce — proved
 * against the real printer in `examples.test.ts`. Loading an example therefore lands the
 * buffer in the app's canonical shape and Format immediately afterwards is a genuine no-op,
 * rather than a one-byte edit that costs the user an undo step. The declared byte count is a
 * property of this module rather than of how the file happens to be stored on disk.
 */

export type ExampleId = 'ecommerce' | 'financial' | 'configuration' | 'geographic';

/** What DESIGN §6.7 prints on the right-hand side of an example row. */
export interface ExampleFacts {
  /** UTF-8 bytes of the pretty-printed text, not of the file on disk. */
  readonly bytes: number;
  /** A trailing newline terminates the last line rather than starting a new one. */
  readonly lines: number;
  /** Nesting depth of the value, root counted as 1. */
  readonly depth: number;
  /** Object keys at every level, arrays excluded. */
  readonly keys: number;
}

export interface ExampleDescriptor {
  readonly id: ExampleId;
  readonly facts: ExampleFacts;
  /**
   * Lazy. Resolves to pretty-printed JSON text.
   *
   * There is no `nameKey` / `descriptionKey` beside it on purpose: `ExamplesDialog` renders
   * `` t(`examples.item.${id}.name`) ``, which TypeScript resolves to a union of four real
   * keys (so a missing one is a compile error) and which `scripts/check-locale-keys.mjs`
   * reads as the wildcard `examples.item.*`. Two literal fields here would be a second place
   * for the same fact to drift from.
   */
  readonly load: () => Promise<string>;
}

const pretty = (value: unknown): string => `${JSON.stringify(value, null, 2)}\n`;

export const examples: readonly ExampleDescriptor[] = [
  {
    // Categories, suppliers and 48 products with nested pricing tiers, per-warehouse
    // inventory, physical specs and a rating histogram — the "complex nested structure"
    // SPEC §5.2 asks for, and the largest of the four.
    id: 'ecommerce',
    facts: { bytes: 89851, lines: 4310, depth: 7, keys: 2689 },
    load: async () => pretty((await import('./examples/ecommerce.json')).default),
  },
  {
    // Four quarters → twelve months → three regions, plus per-employee quota, attainment
    // and closed-won deals. Almost entirely numbers, which is what makes it the useful one
    // for exercising the printer's literal fidelity (ADR-0013).
    id: 'financial',
    facts: { bytes: 26595, lines: 1024, depth: 8, keys: 769 },
    load: async () => pretty((await import('./examples/financial.json')).default),
  },
  {
    // The smallest: a gateway config. Mixed scalar types, `null`s that mean "unset", arrays
    // of objects, and keys containing dots and `$` — the shape most people paste.
    id: 'configuration',
    facts: { bytes: 7629, lines: 364, depth: 6, keys: 224 },
    load: async () => pretty((await import('./examples/configuration.json')).default),
  },
  {
    // A GeoJSON `FeatureCollection`: points, long `LineString`s and polygons with holes.
    // Deep coordinate arrays, which is what makes it worth having before a Map tab exists.
    id: 'geographic',
    facts: { bytes: 33842, lines: 1755, depth: 8, keys: 411 },
    load: async () => pretty((await import('./examples/geographic.json')).default),
  },
];

export function findExample(id: string): ExampleDescriptor | undefined {
  return examples.find((example) => example.id === id);
}

/** Rejects on an unknown id rather than resolving to an empty buffer. */
export async function loadExample(id: string): Promise<string> {
  const example = findExample(id);
  if (example === undefined) throw new Error(`Unknown example: ${id}`);
  return example.load();
}

/** The undo-step label a loaded example carries. One spelling, shared with the dialog. */
export const EXAMPLE_LABEL = 'document.loadExample';
